/**
 * The dashboard opens with *you*.
 *
 * It previously opened with club-wide aggregate charts — true, but not personal.
 * Your next tasting, whether you've RSVP'd, what you owe, and what's unread are
 * the four things a member actually came to check.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { tastingsApi } from '../api/tastings';
import { billingApi } from '../api/billing';
import { inboxApi } from '../api/comms';
import { useAuth } from '../auth/AuthProvider';
import { eventDate } from '../lib/format';
import { Badge } from './ui';

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function Tile({
  label,
  children,
  to,
}: {
  label: string;
  children: React.ReactNode;
  to?: string;
}) {
  const inner = (
    <div className="card h-full p-4 transition hover:border-gold-600/40">
      <p className="text-[11px] uppercase tracking-wide text-cream-300/45">{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export function YourClub() {
  const { user } = useAuth();

  const events = useQuery({ queryKey: ['events'], queryFn: () => tastingsApi.list() });
  const unread = useQuery({
    queryKey: ['inbox', 'unread-count'],
    queryFn: () => inboxApi.unreadCount(),
  });
  const charges = useQuery({
    queryKey: ['my-charges', user?.id],
    queryFn: () => billingApi.listCharges({ personId: user!.id }),
    enabled: !!user,
  });

  const upcoming = (events.data ?? [])
    .filter((e) => e.status === 'upcoming' || e.status === 'live')
    .sort((a, b) => (a.eventDate ?? '').localeCompare(b.eventDate ?? ''))[0];

  const live = (events.data ?? []).find((e) => e.status === 'live');

  const outstanding = (charges.data?.data ?? [])
    .filter((c) => c.status === 'pending')
    .reduce((sum, c) => sum + Number(c.amount), 0);

  const rsvpSummary = useQuery({
    queryKey: ['rsvp-summary', upcoming?.id],
    queryFn: () => tastingsApi.rsvpSummary(upcoming!.id),
    enabled: !!upcoming,
  });

  const myStatus = rsvpSummary.data?.myStatus ?? null;

  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl text-cream-50">
        Good to see you, {user?.firstName}.
      </h2>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {live ? (
          <Tile label="Happening now" to={`/live/${live.id}`}>
            <p className="truncate text-sm text-cream-50">{live.title}</p>
            <p className="mt-1 text-xs text-gold-400">Scoring is open — join →</p>
          </Tile>
        ) : upcoming ? (
          <Tile label="Your next tasting" to={`/tastings/${upcoming.id}`}>
            <p className="truncate text-sm text-cream-50">{upcoming.title}</p>
            <p className="mt-1 text-xs text-cream-300/60">{eventDate(upcoming)}</p>
          </Tile>
        ) : (
          <Tile label="Your next tasting">
            <p className="text-sm text-cream-300/50">Nothing on the calendar yet.</p>
          </Tile>
        )}

        <Tile label="Your RSVP" to={upcoming ? `/tastings/${upcoming.id}` : undefined}>
          {!upcoming ? (
            <p className="text-sm text-cream-300/50">—</p>
          ) : myStatus === 'registered' ? (
            <Badge tone="success">You're going</Badge>
          ) : myStatus === 'waitlist' ? (
            <Badge tone="warning">Waitlisted</Badge>
          ) : (
            <>
              <Badge tone="warning">Not yet</Badge>
              <p className="mt-1 text-xs text-gold-400">Reserve your seat →</p>
            </>
          )}
        </Tile>

        <Tile label="Your dues" to="/account">
          {outstanding > 0 ? (
            <>
              <p className="font-display text-xl text-warning">{money(outstanding)}</p>
              <p className="mt-0.5 text-xs text-gold-400">Settle it →</p>
            </>
          ) : (
            <Badge tone="success">Paid up</Badge>
          )}
        </Tile>

        <Tile label="Unread messages" to="/inbox">
          {(unread.data ?? 0) > 0 ? (
            <>
              <p className="font-display text-xl text-cream-50">{unread.data}</p>
              <p className="mt-0.5 text-xs text-gold-400">Read them →</p>
            </>
          ) : (
            <p className="text-sm text-cream-300/50">All caught up.</p>
          )}
        </Tile>
      </div>
    </section>
  );
}
